import { useEffect, useMemo, useState } from "react";
import { supabase } from "@vinoga/db";
import { Search, X, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type GrapeRow = {
  id: string;
  slug: string;
  translations: { lv?: { name?: string }; en?: { name?: string }; ru?: { name?: string } } | null;
  description: string | null;
  position: number;
  active: boolean;
  product_count?: number;
};

export function GrapeVarieties() {
  const t = useT();
  const { toast } = useToast();
  const [rows, setRows] = useState<GrapeRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<GrapeRow | null>(null);

  const refresh = async () => {
    setLoading(true);
    const [g, pgv] = await Promise.all([
      supabase
        .from("grape_varieties")
        .select("*")
        .order("position", { ascending: true }),
      supabase.from("product_grape_varieties").select("grape_variety_id"),
    ]);
    if (g.error) {
      toast({
        title: t("grapes.errorLoading", "Failed to load grape varieties"),
        description: g.error.message,
        variant: "destructive",
      });
    } else {
      setRows((g.data ?? []) as GrapeRow[]);
    }
    const c: Record<string, number> = {};
    (pgv.data ?? []).forEach((r: any) => {
      c[r.grape_variety_id] = (c[r.grape_variety_id] ?? 0) + 1;
    });
    setCounts(c);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.slug.toLowerCase().includes(s) ||
        (r.translations?.lv?.name ?? "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const save = async (g: GrapeRow) => {
    const { error } = await supabase
      .from("grape_varieties")
      .update({
        slug: g.slug,
        translations: g.translations,
        description: g.description,
        active: g.active,
      })
      .eq("id", g.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("common.saved", "Saved") });
    setEditing(null);
    refresh();
  };

  const remove = async (id: string) => {
    const used = counts[id] ?? 0;
    if (used > 0) {
      toast({
        title: t("grapes.cantDelete", "Cannot delete"),
        description: t(
          "grapes.inUse",
          `This grape is used by ${used} product(s). Reassign products first.`
        ),
        variant: "destructive",
      });
      return;
    }
    if (!confirm(t("grapes.confirmDelete", "Delete this grape variety?"))) return;
    const { error } = await supabase.from("grape_varieties").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Delete failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    refresh();
  };

  const createNew = async () => {
    const name = prompt(t("grapes.newName", "Name (LV)"));
    if (!name) return;
    const slug = name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const { error } = await supabase
      .from("grape_varieties")
      .insert({ slug, translations: { lv: { name } }, active: true });
    if (error) {
      toast({
        title: t("common.createFailed", "Create failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-xl font-bold text-foreground">
            {t("grapes.title", "Grape varieties")}
            {rows.length > 0 && (
              <span className="ml-2 font-body text-sm font-normal text-muted-foreground">
                ({rows.length})
              </span>
            )}
          </h1>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t("grapes.searchPlaceholder", "Search by name or slug...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[260px] pl-8 pr-8 font-body text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <Button size="sm" onClick={createNew}>
          <Plus size={16} />
          {t("grapes.add", "Add grape")}
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("grapes.colSlug", "Slug")}</TableHead>
              <TableHead>{t("grapes.colNameLv", "Name (LV)")}</TableHead>
              <TableHead className="text-right">
                {t("grapes.colUsedIn", "Used in products")}
              </TableHead>
              <TableHead>{t("grapes.colStatus", "Status")}</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  {t("common.loading", "Loading…")}
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  {t("grapes.empty", "No grape varieties.")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((g) =>
                editing?.id === g.id ? (
                  <TableRow key={g.id} className="bg-muted/30">
                    <TableCell>
                      <Input
                        value={editing.slug}
                        onChange={(e) =>
                          setEditing({ ...editing, slug: e.target.value })
                        }
                        className="h-7 font-mono text-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editing.translations?.lv?.name ?? ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            translations: {
                              ...editing.translations,
                              lv: {
                                ...(editing.translations?.lv ?? {}),
                                name: e.target.value,
                              },
                            },
                          })
                        }
                        className="h-7 font-body text-sm"
                      />
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {counts[g.id] ?? 0}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() =>
                          setEditing({ ...editing, active: !editing.active })
                        }
                        className="cursor-pointer"
                      >
                        <Badge variant={editing.active ? "default" : "secondary"}>
                          {editing.active
                            ? t("grapes.statusActive", "active")
                            : t("grapes.statusInactive", "inactive")}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => save(editing)}
                        className="h-7"
                      >
                        <Save size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(null)}
                        className="h-7"
                      >
                        <X size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={g.id} className="cursor-pointer hover:bg-muted/30">
                    <TableCell
                      className="font-mono text-xs"
                      onClick={() => setEditing(g)}
                    >
                      {g.slug}
                    </TableCell>
                    <TableCell className="font-medium" onClick={() => setEditing(g)}>
                      {g.translations?.lv?.name ?? "—"}
                    </TableCell>
                    <TableCell
                      className="text-right text-muted-foreground"
                      onClick={() => setEditing(g)}
                    >
                      {counts[g.id] ?? 0}
                    </TableCell>
                    <TableCell onClick={() => setEditing(g)}>
                      <Badge variant={g.active ? "default" : "secondary"}>
                        {g.active
                          ? t("grapes.statusActive", "active")
                          : t("grapes.statusInactive", "inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(g.id)}
                        className="h-7 hover:text-destructive"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
